<template>
  <transition name="drawer" appear>
    <div v-if="isOpen" class="fixed inset-0 z-50">
      <div class="backdrop absolute inset-0" @click="close" />
      <nav
        ref="mobileDrawerRef"
        class="mobile-drawer w-80 h-full overflow-y-auto bg-white flex flex-col"
        tabindex="-1"
        role="dialog"
        aria-modal="true"
      >
        <div
          class="flex items-center justify-between h-12 pl-2 pr-3 font-medium leading-snug uppercase bg-putty fluid-xs shrink-0"
        >
          <RegionAndLanguageIndicator @click.native="close" :title="$t('localeBar.title')" />
          <button
            class="flex items-center h-full px-2 font-medium leading-snug uppercase fluid-xs"
            @click="close"
            :title="$t('common.close')"
          >
            <CloseIcon class="w-4" />
          </button>
        </div>
        <TopLevelMobileNav
          ref="refTopLevelMobileNav"
          class="border-t"
          @child-navigation-opened="handleChildNavigationOpened"
          @child-navigation-closed="handleChildNavigationClosed"
        />
        <MobileContactBubble />
      </nav>
    </div>
  </transition>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from 'vue';
import { useMainNavStore } from '@/stores/main-nav';
import { useI18n } from 'vue-i18n';
import { useRuntimeConfig } from '#app';

import AccountIcon from '@/components/layout/header/AccountIcon';
import CloseIcon from '@/components/icons/CloseIcon';
import Expandable from '@/components/common/Expandable';
import HelpIcon from '@/components/layout/header/HelpIcon';
import IconChevron from '@/components/layout/header/IconChevron';
import MobileContactBubble from '@/components/layout/header/mobileDrawer/MobileContactBubble';
import RegionAndLanguageIndicator from '@/components/layout/header/RegionAndLanguageIndicator';
import TopLevelMobileNav from '@/components/layout/header/mobileDrawer/TopLevelMobileNav';
import { useFocusTrap } from '@vueuse/integrations/useFocusTrap';

// Store
const mainNavStore = useMainNavStore();
const isOpen = computed(() => mainNavStore.isOpen);
const open = () => mainNavStore.open();
const close = () => mainNavStore.close();

// Config
const config = useRuntimeConfig();
const accountEnabled = computed(() => config.public.auth.isEnabled);

// i18n
const { locale } = useI18n();
const currentLang = computed(() => locale.value);

// Focus trap
const mobileDrawerRef = ref(null);
const refTopLevelMobileNav = ref(null);
const activeChildNavigation = ref(null);

const isTopSectionOpen = ref(false);

// Focus trap setup
const { activate, deactivate } = useFocusTrap(mobileDrawerRef);

const handleChildNavigationClosed = (data) => {
  // Clear the active child navigation
  activeChildNavigation.value = null;
};

const handleChildNavigationOpened = (childNav) => {
  activeChildNavigation.value = childNav;
};

const handleKeyDown = (event) => {
  if (event.key === 'Escape' && isOpen.value) {
    if (activeChildNavigation.value) {
      // If there's an active child navigation, just close it
      activeChildNavigation.value = null;
    } else {
      // Otherwise close the drawer
      close();
    }
  }
};

// Lifecycle hooks
onMounted(() => {
  window.addEventListener('keydown', handleKeyDown);
});

onUnmounted(() => {
  window.removeEventListener('keydown', handleKeyDown);
});

// Watch for drawer open/close
watch(isOpen, (newValue) => {
  if (newValue) {
    nextTick(() => {
      activate();
    });
  } else {
    deactivate();
  }
});
</script>

<style scoped>
@reference "~/assets/css/main.css";
.drawer-enter-active,
.drawer-leave-active {
  transition: all 300ms linear;
}

.mobile-drawer {
  transition: transform 300ms ease;
}

.drawer-enter .backdrop,
.drawer-leave-to .backdrop {
  @apply opacity-0;
}

.drawer-enter .mobile-drawer,
.drawer-leave-to .mobile-drawer {
  transform: translateX(-100%);
  @apply translate-x-[-100%];
}
.backdrop {
  transition: opacity 300ms ease;
  background-color: rgba(0, 0, 0, 0.75);
  @apply z-[-1];
}
</style>
